import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  ErrorPolicy,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { getLogger } from '@summer-health/shared/next-logger';
import { AuthError } from '@summer-health/types/auth';
import { get } from 'lodash';

export { logApolloError } from './log-error';

const logger = getLogger('apollo-client');

const errorPolicy: ErrorPolicy = 'all';

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: (count, operation, error): boolean => {
    // RetryLink is only used for network and server errors
    // It does not retry for regular GraphQL errors

    const isMutation =
      operation &&
      operation.query &&
      operation.query.definitions &&
      Array.isArray(operation.query.definitions) &&
      operation.query.definitions.some(
        (def) =>
          def.kind === 'OperationDefinition' && def.operation === 'mutation',
      );

    if (!error) {
      // I don't even think this case is possible
      // pretty sure there is always an error
      return false;
    }

    // TypeErrors are usually a client side network issue
    if (error.name === 'TypeError') {
      const retry = count < 3;
      logger.warn(
        { err: error, count, retry },
        'Retry attempt: TypeError detected',
      );
      return retry;
    }

    // 500 issues are usually a connection issue to API
    if (error.statusCode?.toString()?.startsWith('5')) {
      // Real 500s will be logged in the API as errors
      // since this could be a temporary network blip just warn
      const retry = count < 2;
      logger.warn({ err: error, count, retry }, `Retry attempt: 5xx detected`);
      return retry;
    }

    if (error.statusCode) {
      // This is where our 4xx and others http codes go
      // This can happen when there are authentication issues etc
      logger.warn(
        { err: error, count },
        `Retry attempt: Not retrying other status code ${error.statusCode}`,
      );
      return false;
    }

    const maxRetry = isMutation ? 2 : 3;
    const retry = count < maxRetry;

    logger.error(
      { err: error, count, retry },
      `Retry attempt: Unrecognized error event`,
    );
    return retry;
  },
});

const errorLink = onError((error) => {
  const { graphQLErrors, networkError } = error;

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      if (
        ['BAD_REQUEST', 'BAD_USER_INPUT'].includes(extensions?.code as string)
      ) {
        return;
      }

      if (extensions?.code === 'UNAUTHENTICATED') {
        const currentUrl = encodeURIComponent(window.location.href);
        const loginUrl = `/api/auth/login?returnTo=${currentUrl}`;
        window.location.href = loginUrl;
        return;
      }

      logger.error(
        { message, locations, path },
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations,
        )}, Path: ${path}`,
      );
    });
  }
  if (networkError) {
    logger.warn({ networkError }, `[Network error]: ${networkError}`);

    // Redirect to login page if we get a 401
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((networkError as any).statusCode === 401) {
      const currentUrl = encodeURIComponent(window.location.href);
      const loginUrl = `/api/auth/login?returnTo=${currentUrl}`;
      window.location.href = loginUrl;
    }
  }
});

const httpLink = new HttpLink({
  uri: '/api/graphql',
});

export const extractAuthError = (errors: ApolloError): AuthError => {
  // per docs: https://www.apollographql.com/docs/react/api/react/hooks/#usequery-parameters-options-onerror
  // This function is passed an ApolloError object that contains
  // either
  //   a networkError object
  // or
  //   a graphQLErrors array,
  // depending on the error(s) that occurred.
  const authGraphQLErrors = Array.isArray(errors)
    ? errors
    : errors.graphQLErrors || [];
  // Keep network errors for backwards compatibility while launching.
  const authNetworkErrors = get(errors, 'networkError.result.errors', []);
  const rawAuthError = [...authNetworkErrors, ...authGraphQLErrors]?.find(
    (e) => e.extensions?.originalError?.name === 'AuthError',
  )?.extensions?.originalError;
  if (rawAuthError) {
    return new AuthError(
      rawAuthError.type,
      rawAuthError.message,
      rawAuthError.language,
    );
  }
  return undefined;
};

export const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([retryLink, errorLink, httpLink]),
  defaultOptions: {
    query: {
      errorPolicy,
      fetchPolicy: 'no-cache',
    },
    watchQuery: {
      errorPolicy,
      fetchPolicy: 'no-cache',
    },
    mutate: {
      errorPolicy,
      fetchPolicy: 'no-cache',
    },
  },
});

export const serverClient = new ApolloClient({
  uri: `${process.env.API_BASE_URL}/graphql`,
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      errorPolicy,
      fetchPolicy: 'no-cache',
    },
    watchQuery: {
      errorPolicy,
      fetchPolicy: 'no-cache',
    },
    mutate: {
      errorPolicy,
      fetchPolicy: 'no-cache',
    },
  },
});

/**
 * Error examples
Bad client connection
{
  "name": "TypeError",
  "message": "Failed to fetch",
  "stack": "TypeError: Failed to fetch...."
}

Bad input from server
{
  "name": "ServerError",
  "response": {},
  "statusCode": 401,
  "result": {
      "errors": [
          {
              "message": "Cannot invite yourself.",
              "extensions": {
                  "code": "BAD_USER_INPUT",
                  "response": {
                      "statusCode": 400,
                      "message": "Cannot invite yourself.",
                      "error": "Bad Request"
                  }
              }
          }
      ]
  },
  "message": "Response not successful: Received status code 401",
  "stack": "ServerError: Response not successful: Received status code 401\n    at throwServerError ...."
}

API Unavailable
{
  "name": "ServerError",
  "response": {},
  "statusCode": 500,
  "result": {
      "error": "Unexpected token < in JSON at position 0"
  },
  "message": "Response not successful: Received status code 500",
  "stack": "ServerError: Response not successful: Received status code 500\n    at throwServerError..."
}

Bad query
{
  "name": "ServerError",
  "response": {},
  "statusCode": 400,
  "result": {
      "errors": [
          {
              "message": "Cannot query field \"sponsoredCaregiverInviteIds\" on type \"Spo.....",
              "locations": [
                  {
                      "line": 3,
                      "column": 5
                  }
              ],
              "extensions": {
                  "code": "GRAPHQL_VALIDATION_FAILED",
                  "exception": {
                      "stacktrace": [
                          "GraphQLError: Cannot query field \"sponsoredCaregiverInvite....",
                          "    at Object.Field (/Users/jose/Projects/summer-health/summ...",
                          "    at Object.enter (/Users/jose/Projects/summer-health/summ...",
                          "    at Object.enter (/Users/jose/Projects/summer-health/summ...",
                          "    at visit (/Users/jose/Projects/summer-health/summer-heal...",
                          "    at validate (/Users/jose/Projects/summer-health/summer-h...",
                          "    at validate (/Users/jose/Projects/summer-health/summer-h...",
                          "    at processGraphQLRequest (/Users/jose/Projects/summer-he...",
                          "    at processTicksAndRejections (node:internal/process/task_queues:95:5)",
                          "    at processHTTPRequest (/Users/jose/Projects/summer-healt..."
                      ]
                  }
              }
          }
      ]
  },
  "message": "Response not successful: Received status code 400",
  "stack": "ServerError: Response not successful: Received status code 400\n    at throwServerError..."
}
 */
